import React, { memo, useCallback } from "react";
import { DeleteIcon, ImageIcon } from "../../../../../../AssetComponents/Images";

// Helper function to get image URL - supports both uploaded images (object) and IPFS URLs (string)
const getImageUrl = (image) => {
  if (!image) return null;
  // If it's a string (IPFS URL from draft), return it directly
  if (typeof image === 'string') return image;
  // If it's an object with data_url (newly uploaded), return data_url
  if (image.data_url) return image.data_url;
  return null;
};

const PhotoReviewSection = memo(({
  images,
  removeElement,
  dragStartIdx,
  setDragStartIdx,
  dragEndIdx,
  setDragEndIdx,
  swapElements,
}) => {
  // Memoize handlers to prevent re-renders
  const handleRemove = useCallback((idx) => {
    removeElement(images.slice(), idx);
  }, [removeElement, images]);

  const handleDragStart = useCallback((idx) => {
    setDragStartIdx(idx);
  }, [setDragStartIdx]);

  const handleDragEnter = useCallback((idx) => {
    setDragEndIdx(idx);
  }, [setDragEndIdx]);

  const handleDragEnd = useCallback(() => {
    swapElements(images.slice(), dragStartIdx, dragEndIdx);
  }, [swapElements, images, dragStartIdx, dragEndIdx]);

  return (
    <div className="w-full h-max p-[10px] bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
      {images[0] ? (
        <div className="relative group">
          <div className="absolute bg-white px-[10px] text-[14px] font-normal py-[6px] top-[10px] left-[10px] rounded-[8px]">
            Cover photo
          </div>
          <div
            onClick={() => handleRemove(0)}
            className="group-hover:block hidden shadow-md cursor-pointer absolute bg-white p-[8px] bottom-[10px] right-[10px] rounded-full"
          >
            <DeleteIcon className="w-[16px]" />
          </div>
          <img
            alt=""
            src={getImageUrl(images[0])}
            className="w-full h-[300px] sm:h-[400px] object-cover rounded-[10px]"
            onDragStart={() => handleDragStart(0)}
            onDragEnter={() => handleDragEnter(0)}
            onDragEnd={handleDragEnd}
          />
        </div>
      ) : (
        <div className="flex justify-center items-center w-full h-[300px] sm:h-[400px] border-[2px] border-dashed rounded-[8px]">
          <ImageIcon />
        </div>
      )}

      <div className="h-max grid grid-cols-2 gap-[10px] mt-[10px]">
        {images.slice(1).map((img, idx) => {
          const actualIdx = idx + 1;
          return (
            <div key={actualIdx} className="relative group">
              <div
                onClick={() => handleRemove(actualIdx)}
                className="hidden group-hover:block group shadow-md cursor-pointer absolute bg-white p-[8px] bottom-[10px] right-[10px] rounded-full"
              >
                <DeleteIcon className="w-[16px]" />
              </div>
              <img
                alt=""
                draggable
                src={getImageUrl(images[actualIdx])}
                className="rounded-[8px] w-full h-[140px] sm:h-[180px] object-cover"
                onDragStart={() => handleDragStart(actualIdx)}
                onDragEnter={() => handleDragEnter(actualIdx)}
                onDragEnd={handleDragEnd}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

PhotoReviewSection.displayName = "PhotoReviewSection";

export default PhotoReviewSection;
